import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn, pubsub } from "utils";
import { useCallback, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import log from "logbeacon";
import { EdgenModalCreateAble } from "../../EdgenModal";
import { Input } from "../..";
import { CategoryDetail } from "./CategoryDetail";
import type { MenuSearchModalProps } from "./types";
import { RecommendedArea } from "./RecommendArea";
import { SearchOverview } from "./SearchOverview";
import { useMenuSearchModal } from "./useMenuSearchModal";
import { I18nUtil } from "@utils/i18n";
import { QUERY_KEYS } from "@/src/config";
import Translations from "@translate/components/Menu/MenuSearchModal/MenuSearchModal";
import { favoritesCenter } from "@/src/store/favorite";
import { menuSearchSelection } from "@/src/store/menuSearchSelection";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import { sleep } from "@/src/utils/sleep";
import { usePortfolioPage } from "@/src/hooks/usePortfolioPage";

const I18n = I18nUtil.createScoped(Translations);

const MenuSearchModal: React.FC<MenuSearchModalProps> = ({
  onClose,
  onItemClick: _onItemClick,
  type,
  onCompleted,
  defaultRecommendedTab,
}) => {
  const queryClient = useQueryClient();
  const { refetch } = usePortfolioPage();
  const { clear, selectIds } = useSnapshot(menuSearchSelection);
  // 获取收藏状态数据 - 观察整个 state 对象
  const { userFolders } = useSnapshot(favoritesCenter.state);
  const folderId = userFolders?.[0]?.id || "";
  const selectedCount = selectIds.length;
  const [onFocus, setOnFocus] = useState(false);
  const [loading, setLoading] = useState(false);
  const items = [
    {
      key: "stock" as const,
      label: I18n.t("Stocks"),
    },
    {
      key: "crypto" as const,
      label: I18n.t("Crypto"),
    },
  ];

  // 处理关闭
  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // 使用自定义 Hook 管理状态
  // 传入 handleModalClose，用于在登录成功时自动关闭弹窗
  const {
    searchState,
    currentView,
    visibleCategories,
    getCategoryDetailData,
    hasSearchResults: _hasSearchResults,
    // 统一的滚动容器引用
    scrollContainerRef,
    handleSearchInput,
    handleViewAll,
    handleBack,
    handleClose,
    isLoading,
    handleRecommendedTabChange,
  } = useMenuSearchModal(type, handleModalClose, defaultRecommendedTab);

  // 处理清空搜索
  const handleClearSearch = () => {
    handleSearchInput("");
  };

  // 最终的关闭处理函数（同时清理内部状态和关闭弹窗）
  const handleFinalClose = useCallback(() => {
    // 仅清空勾选状态
    clear();
    handleClose();
    handleModalClose();
  }, [handleClose, handleModalClose, clear]);

  const handleAddFavorites = useCallback(async () => {
    setLoading(true);
    pubsub.emit(pubSubKeyMap.COLLECT_CHANGE);
    favoritesCenter.batchAddAssets([...selectIds], folderId);
    await sleep(1500);
    handleFinalClose();
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.AGENT_REPORTS_TIMELINE],
    });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AGENT_REPORT_LIST] });
    refetch();
    onCompleted?.();
  }, [
    handleFinalClose,
    selectIds,
    folderId,
    queryClient,
    refetch,
    onCompleted,
  ]);

  return (
    <div
      className={cn(
        "flex flex-col h-full relative w-full max-h-[calc(100vh_-_160px)] pb-safe",
      )}
    >
      {/* 搜索输入区域 - 固定在顶部 */}
      <div className="sticky top-0 z-10 md:px-6 md:py-6 px-4 py-4 flex flex-row items-center gap-3 bg-background_solid_secondary">
        <Input
          prefixClassName="text-content_primary"
          placeholder={
            type === "normal"
              ? I18n.t(
                  "Search Stock Themes / Crypto Themes / Stocks / Crypto / News",
                )
              : I18n.t("Search Themes / Stocks / Crypto")
          }
          value={searchState.keywords}
          onChange={handleSearchInput}
          onClear={handleClearSearch}
          containerClassName={cn("h-9 bg-background_solid_tertiary", {
            "border-background_white_overlay20": onFocus,
            "border-background_solid_secondary": !onFocus,
          })}
          data-testid="search-input"
          autoFocus={false}
          onFocus={(focus) => {
            setOnFocus(focus);
          }}
        />
        <div
          data-openreplay-label={`MenuSearchModal · MenuSearchModal · div | ${I18n.t("Cancel")}`}
          className="button_m text-content_tertiary cursor-pointer min-w-fit"
          onClick={handleFinalClose}
        >
          {I18n.t("Cancel")}
        </div>
      </div>
      {/* 返回按钮区域 - 吸顶显示，仅在分类详细页面显示 */}
      {currentView.showCategoryDetail && (
        <div className="sticky top-[68px] z-10 bg-background_solid_secondary md:px-6 px-4 pb-1">
          <button
            aria-label="Menu Search Modal"
            data-openreplay-label="Menu Search Modal Button"
            id="btn-menu-search-modal"
            onClick={handleBack}
            className="flex items-center gap-2 text-content_primary hover:text-content_secondary transition-colors"
          >
            <Icon
              name="left-arrow-alt"
              size={16}
              color={ospThemes.colors.content_primary}
            />
            <span className="body_s">{I18n.t("Back")}</span>
          </button>
        </div>
      )}
      {currentView.showRecommended && (
        <div
          className={cn(
            "flex justify-start border-b border-border_white_transparent10 w-full md:px-6 px-4 sticky top-[68px] bg-background_solid_secondary",
          )}
        >
          <tab-indicator
            className="flex gap-6 overflow-x-auto scrollbar-hide [--underline-color:var(--edgen-border\_brand\_solid)] [--animation-duration:0.2s]"
            effect="underline"
          >
            {items.map((item) => (
              <div
                data-openreplay-label="MenuSearchModal · MenuSearchModal · div"
                key={item.key}
                role="tab-item"
                data-actived={searchState.recommendedTab === item.key}
                className="tab_item flex-shrink-0"
                onClick={() => {
                  handleRecommendedTabChange(item.key);
                }}
              >
                <span className="py-[6px] button_l text-content_primary">
                  {item.label}
                </span>
              </div>
            ))}
          </tab-indicator>
        </div>
      )}
      {/* 统一的滚动容器 - 容器始终存在，只渲染当前视图内容 */}
      {/* 容器不重建，ref 始终有效，滚动 100% 可靠 */}
      <div
        ref={scrollContainerRef}
        className="overflow-y-auto no-scrollbar flex-1"
        // style={{
        //   maxHeight: "calc(100vh - 120px)",
        //   minHeight: "312px",
        // }}
      >
        {/* 推荐区域 */}
        {currentView.showRecommended && (
          <RecommendedArea
            onClose={handleFinalClose}
            activeTab={searchState.recommendedTab}
            onTabChange={handleRecommendedTabChange}
            type={type}
          />
        )}

        {/* 搜索概览 */}
        {currentView.showSearchOverview && (
          <SearchOverview
            categories={visibleCategories}
            onViewAll={handleViewAll}
            onClose={handleFinalClose}
            isLoading={isLoading}
            originType={type}
          />
        )}

        {/* 分类详细 */}
        {currentView.showCategoryDetail &&
          searchState.selectedCategory &&
          (() => {
            const categoryData = getCategoryDetailData(
              searchState.selectedCategory,
            );
            return categoryData ? (
              <CategoryDetail
                categoryType={searchState.selectedCategory}
                title={searchState.categoryTitle || ""}
                data={categoryData.data}
                onClose={handleFinalClose}
                originType={type}
              />
            ) : null;
          })()}
      </div>
      {type === "portfolio" && (
        <div className="sticky bottom-0 z-10 bg-background_solid_secondary md:p-6 px-4 pb-4 pt-4 md:pt-0">
          <div className="flex items-center justify-between">
            <div
              className={cn("body_medium_m text-content_primary", {
                "opacity-0": !selectedCount,
              })}
            >
              {`${selectedCount} `}
              {I18n.t("Selected")}
            </div>
            <button
              aria-label="Menu Search Modal"
              data-openreplay-label="Menu Search Modal Button"
              id="btn-menu-search-modal-2"
              className={cn(
                "primary_button  sm_button w-[104px] button_m group",
                { "cursor-not-allowed": !selectedCount },
              )}
              disabled={!selectedCount || loading}
              onClick={handleAddFavorites}
              data-loading={loading}
            >
              <span className="group-loading:hidden text-content_inverse_primary">
                {I18n.t("Add Assets")}
              </span>
              <Icon
                name="ic_loading2"
                size={20}
                color={ospThemes.colors.content_inverse_primary}
                className={cn("hidden animate-spin group-loading:block")}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface MenuSearchModalCreateAbleProps {
  type?: "normal" | "portfolio";
  defaultRecommendedTab?: "stock" | "crypto";
  onCompleted?: () => void;
}

export const MenuSearchModalCreateAble = async (
  props: MenuSearchModalCreateAbleProps,
): Promise<{
  keyword: string;
  token: string;
}> => {
  const { type, onCompleted, defaultRecommendedTab, ...otherProps } = props;
  const onClose = () => {
    const input = document?.querySelector(
      "[data-testid='search-input']",
    ) as HTMLInputElement;
    const keyWord = input?.value || "";
    // 本条日志用于跟踪用户高频搜索关键词
    log.debug("[MenuSearchModal] close modal search keywords:", keyWord);
    pubsub.emit(pubSubKeyMap.MENU_SEARCH_MODAL_CLOSED);
    EdgenModalCreateAble.end(false);
  };
  return new Promise((resolve) => {
    pubsub.emit(pubSubKeyMap.MENU_SEARCH_MODAL_OPEN);
    EdgenModalCreateAble.call({
      children: (
        <MenuSearchModal
          type={type}
          defaultRecommendedTab={defaultRecommendedTab}
          {...otherProps}
          onClose={onClose}
          onItemClick={(item: { keyword: string; token: string }) => {
            onClose();
            resolve(item);
          }}
          onCompleted={onCompleted}
        />
      ),
      domClassName: "edgen-modal",
      containerClassName: "max-w-full w-full md:w-[800px]",
      className: "[--modal-radius:16px_16px_0_0] md:[--modal-radius:16px]",
      contentStyle: {
        borderRadius: "var(--modal-radius)",
        backgroundColor: ospThemes.colors.background_solid_secondary,
        width: "100%",
        padding: 0,
        overflow: "hidden",
      },
      closeStyle: {
        backgroundColor: ospThemes.colors.background_solid_secondary,
      },
      zIndex: 900,
      destroyOnClose: true,
      onCancel: onClose,
    });
  });
};
